/* OnSumn v2 — Glass Design Tokens & Mixins */

/* Glass material: translucent layered bg + inner glow + blur */
const GLASS = {
  // Standard glass panel (dark)
  dark: {
    background: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), linear-gradient(rgba(51,51,51,0.35), rgba(51,51,51,0.35)), linear-gradient(rgba(153,153,153,0.15), rgba(153,153,153,0.15))',
    boxShadow: 'inset 0px 0px 20px 0px rgba(242,242,242,0.06), inset 0px 0px 3px 0px rgba(255,255,255,0.12), inset -1px -1px 0.5px -1px rgba(255,255,255,0.25), inset 1px 1px 0.5px -1px rgba(255,255,255,0.25), inset -1px -1px 0px -0.5px rgba(38,38,38,0.5), inset 1px 1px 0px -0.5px rgba(51,51,51,0.5)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
  },
  // Light mode glass panel.
  //
  // OPACITY FLOOR (Goal E, 2026-07-29 — Iris MEDIUM-3, Lux's values): the
  // bottom layer is an OPAQUE warm-cream base (#F5F0E7, the venue light
  // surface — an existing system hex, no new color). Without it the pane's
  // effective background was whatever the page put behind the blur, and a
  // dark poster dropped every light-mode amber to 2.89:1 — below the 4.5:1
  // AA bar on the mobile-primary Spotlight path. With the floor the
  // composite is a CONSTANT #F6F5F1: amber 6.78:1, muted ink 4.73:1,
  // ctrlBorder 4.09:1, backdrop-independent (tests/glass-floor.test.js pins
  // the whole audited set against a #000 backdrop). On venue pages the
  // composite is byte-identical to what the pane already rendered over the
  // cream surface — the floor just makes every page the designed case. The
  // translucent white/gray tints and the blur stay on top so the material
  // still reads as glass; dark mode measured robust (7.86–12.66:1) and is
  // untouched — parity closes by lifting light, never dimming dark.
  light: {
    background: 'linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), linear-gradient(rgba(200,200,200,0.2), rgba(200,200,200,0.2)), linear-gradient(#F5F0E7, #F5F0E7)',
    boxShadow: 'inset 0px 0px 20px 0px rgba(255,255,255,0.3), inset 0px 0px 3px 0px rgba(255,255,255,0.4), inset -1px -1px 0.5px -1px rgba(255,255,255,0.6), inset 1px 1px 0.5px -1px rgba(255,255,255,0.6), 0 1px 3px rgba(0,0,0,0.06)',
    backdropFilter: 'blur(40px)',
    WebkitBackdropFilter: 'blur(40px)',
  },
  // Subtle glass for cards
  cardDark: {
    background: 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), linear-gradient(rgba(51,51,51,0.4), rgba(51,51,51,0.4)), linear-gradient(rgba(153,153,153,0.1), rgba(153,153,153,0.1))',
    boxShadow: 'inset 0px 0px 16px 0px rgba(242,242,242,0.04), inset 0px 0px 2px 0px rgba(255,255,255,0.1), inset -1px -1px 0.5px -1px rgba(255,255,255,0.15), inset 1px 1px 0.5px -1px rgba(255,255,255,0.15)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  cardLight: {
    background: 'linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), linear-gradient(rgba(200,200,200,0.15), rgba(200,200,200,0.15))',
    boxShadow: 'inset 0px 0px 16px 0px rgba(255,255,255,0.25), inset 0px 0px 2px 0px rgba(255,255,255,0.35), inset -1px -1px 0.5px -1px rgba(255,255,255,0.5), inset 1px 1px 0.5px -1px rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.04)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  // Quiet card — the Von Restorff "field" surface (non-hero cards on the
  // homepage grid). Same glass material as card — blur intact, layered
  // tint intact — with the inner white glow cut to ~half strength so the
  // field recedes and the single hero's ring carries the eye. Fully-flat
  // 'solid' was the first cut and de-energized the whole page when every
  // non-hero card wore it; this keeps the glass alive, just dimmer.
  quietDark: {
    background: 'linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), linear-gradient(rgba(51,51,51,0.4), rgba(51,51,51,0.4)), linear-gradient(rgba(153,153,153,0.06), rgba(153,153,153,0.06))',
    boxShadow: 'inset 0px 0px 12px 0px rgba(242,242,242,0.02), inset 0px 0px 2px 0px rgba(255,255,255,0.05), inset -1px -1px 0.5px -1px rgba(255,255,255,0.08), inset 1px 1px 0.5px -1px rgba(255,255,255,0.08)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  quietLight: {
    background: 'linear-gradient(rgba(255,255,255,0.6), rgba(255,255,255,0.6)), linear-gradient(rgba(200,200,200,0.15), rgba(200,200,200,0.15))',
    boxShadow: 'inset 0px 0px 12px 0px rgba(255,255,255,0.12), inset 0px 0px 2px 0px rgba(255,255,255,0.18), inset -1px -1px 0.5px -1px rgba(255,255,255,0.25), inset 1px 1px 0.5px -1px rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.04)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  // Hero card — the Von Restorff "one lifted, lit card" surface (PR #88,
  // reworked fully-inset 2026-07-17). The feed carousel (.events-carousel,
  // index.html) is an overflow-x scroll container with padding 2px 0 8px —
  // any non-inset shadow layer gets cropped at the top/bottom/leading
  // edges, which is exactly how the v2 outer ring + 44px bloom shipped as
  // a sliced frame on the first card. Nothing in these tokens may paint
  // outside the card's box; test/hero-inset.test.js fails if a non-inset
  // layer returns. The 2px frame itself does NOT live here: the poster
  // <img> is position:absolute inset:0 ABOVE the card background, and
  // inset box-shadows paint UNDER children — a token-level ring would
  // vanish behind the poster across the whole media area. The frame is
  // painted by the article[data-emphasis="hero"]::after overlay injected
  // below (over the poster at z-index:1, under the z-index:2 chips),
  // shared by both hero variants; ::before carries Goal 2's breathing
  // glow (hero-live only). The brighter tint than the field (0.58 vs 0.70/0.72
  // black overlays) stays — it is in-bounds and carries the lift. Still
  // deliberately orange-free (orange is never rare on this page).
  heroDark: {
    background: 'linear-gradient(rgba(0,0,0,0.58), rgba(0,0,0,0.58)), linear-gradient(rgba(51,51,51,0.45), rgba(51,51,51,0.45)), linear-gradient(rgba(153,153,153,0.16), rgba(153,153,153,0.16))',
    boxShadow: 'inset 0px 0px 18px 0px rgba(242,242,242,0.07), inset 0px 0px 3px 0px rgba(255,255,255,0.16), inset 0 1px 0 rgba(255,255,255,0.14)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  heroLight: {
    background: 'linear-gradient(rgba(255,255,255,0.78), rgba(255,255,255,0.78)), linear-gradient(rgba(200,200,200,0.1), rgba(200,200,200,0.1))',
    boxShadow: 'inset 0px 0px 18px 0px rgba(255,255,255,0.35), inset 0px 0px 3px 0px rgba(255,255,255,0.45), inset 0 1px 0 rgba(255,255,255,0.8)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  // Button glass — dark (unchanged)
  btnDark: {
    background: 'linear-gradient(rgba(255,255,255,0.1), rgba(255,255,255,0.06))',
    boxShadow: 'inset 0px 0px 8px 0px rgba(255,255,255,0.06), inset -1px -1px 0.5px -1px rgba(255,255,255,0.2), inset 1px 1px 0.5px -1px rgba(255,255,255,0.2)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  // Button glass — light: soft white neon glow at 60% opacity
  btnLight: {
    background: 'linear-gradient(rgba(255,255,255,0.72), rgba(255,255,255,0.55))',
    boxShadow: '0 0 12px rgba(255,255,255,0.6), 0 0 28px rgba(255,255,255,0.25), inset 0px 0px 10px 0px rgba(255,255,255,0.5), inset -1px -1px 0.5px -1px rgba(255,255,255,0.6), inset 1px 1px 0.5px -1px rgba(255,255,255,0.6), 0 1px 3px rgba(0,0,0,0.08)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
  },
  // Solid card — no blur, simple background
  solidDark: {
    background: 'rgba(18, 20, 20, 0.95)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  solidLight: {
    background: 'rgba(255, 255, 255, 0.92)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
};

function glassStyle(darkMode, variant = 'card') {
  if (variant === 'panel') return darkMode ? GLASS.dark : GLASS.light;
  if (variant === 'btn') return darkMode ? GLASS.btnDark : GLASS.btnLight;
  if (variant === 'hero') return darkMode ? GLASS.heroDark : GLASS.heroLight;
  if (variant === 'quiet') return darkMode ? GLASS.quietDark : GLASS.quietLight;
  if (variant === 'solid') return darkMode ? GLASS.solidDark : GLASS.solidLight;
  return darkMode ? GLASS.cardDark : GLASS.cardLight;
}

window.GLASS = GLASS;
window.glassStyle = glassStyle;

/* ─── Glass utility classes (Architect ask #11 from Phase B queue) ─────
 * Inject `.glass-{panel,card,btn,solid}` classes once, theme-scoped via
 * `:root[data-theme]`. Components that don't need any JS-conditional
 * overrides on the glass surface can now use `className` instead of
 * spreading the object — saves a fresh style-object allocation on every
 * render of those call sites.
 *
 * IMPORTANT: existing call sites that combine `...glassStyle()` with
 * inline overrides (theme-aware background gradient, hover state, etc.)
 * stay as-is. The `GLASS` constants are already module-scoped (one shared
 * object reference per variant) so there is no garbage churn — the only
 * win available there would be DOM payload reduction, which is small
 * (~150 chars/card) and risks visual regression to chase.
 *
 * The classes match `GLASS` byte-for-byte so a future refactor can swap
 * in className → utility one component at a time with no visual diff.
 *
 * Lives here (not colors_and_type.css) because:
 *   1. Keeps GLASS source-of-truth in one file (avoids drift if anyone
 *      edits the dark/light values without updating both).
 *   2. The injection runs once on first GlassTokens.jsx load, picked up
 *      by index.html, venue.html, admin.html, and flows.html alike.
 */
(function injectGlassUtilitiesOnce() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('onsumn-glass-utilities')) return;
  var style = document.createElement('style');
  style.id = 'onsumn-glass-utilities';

  // Helper — turn a GLASS variant object into a CSS rule body. The keys
  // we care about (background, boxShadow, backdropFilter,
  // WebkitBackdropFilter) all serialize 1:1; React's `boxShadow` is plain
  // CSS `box-shadow`, etc.
  function ruleBody(v) {
    return (
      'background:' + v.background + ';' +
      'box-shadow:' + v.boxShadow + ';' +
      (v.backdropFilter ? 'backdrop-filter:' + v.backdropFilter + ';' : '') +
      (v.WebkitBackdropFilter ? '-webkit-backdrop-filter:' + v.WebkitBackdropFilter + ';' : '')
    );
  }

  style.textContent =
    // Defaults assume dark; light variants live under [data-theme=light].
    '.glass-panel{'  + ruleBody(GLASS.dark)      + '}' +
    '.glass-card{'   + ruleBody(GLASS.cardDark)  + '}' +
    '.glass-btn{'    + ruleBody(GLASS.btnDark)   + '}' +
    '.glass-hero{'   + ruleBody(GLASS.heroDark)  + '}' +
    '.glass-quiet{'  + ruleBody(GLASS.quietDark) + '}' +
    '.glass-solid{'  + ruleBody(GLASS.solidDark) + '}' +
    ':root[data-theme="light"] .glass-panel{' + ruleBody(GLASS.light)      + '}' +
    ':root[data-theme="light"] .glass-card{'  + ruleBody(GLASS.cardLight)  + '}' +
    ':root[data-theme="light"] .glass-btn{'   + ruleBody(GLASS.btnLight)   + '}' +
    ':root[data-theme="light"] .glass-hero{'  + ruleBody(GLASS.heroLight)  + '}' +
    ':root[data-theme="light"] .glass-quiet{' + ruleBody(GLASS.quietLight) + '}' +
    ':root[data-theme="light"] .glass-solid{' + ruleBody(GLASS.solidLight) + '}' +

    // Hero FRAME overlay — NOT part of the GLASS byte-match above. The
    // frame must paint ABOVE the full-bleed poster (an inset shadow on
    // the article paints under its children), so it rides an ::after
    // overlay keyed off the data-emphasis attr EventCard already stamps.
    // Double-ring (2px frame + 1px counter-stroke) keeps the frame's edge
    // ≥3:1 against any poster pixel — same vocabulary as the site's
    // focus-double-ring (Iris). Every layer is inset: the carousel crops
    // anything outer (that clip IS the bug this replaces). z-index 1 =
    // above the poster (z-auto), below the z-index:2 chips/date bubble.
    // position:relative is a guard mirror of EventCard's inline style so
    // the overlay can never silently anchor to an ancestor. The frame is
    // SHARED by both hero variants — 'hero' (static) and 'hero-live'
    // (breathing) — and is NEVER animated: a pulsing frame reads as
    // flicker, not life.
    'article[data-emphasis="hero"],article[data-emphasis="hero-live"]{position:relative;}' +
    'article[data-emphasis="hero"]::after,article[data-emphasis="hero-live"]::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:1;box-shadow:inset 0 0 0 2px rgba(255,255,255,0.92),inset 0 0 0 3px rgba(0,0,0,0.35),inset 0 0 30px 4px rgba(255,255,255,0.12);}' +
    ':root[data-theme="light"] article[data-emphasis="hero"]::after,:root[data-theme="light"] article[data-emphasis="hero-live"]::after{box-shadow:inset 0 0 0 2px rgba(10,10,10,0.92),inset 0 0 0 3px rgba(255,255,255,0.55),inset 0 0 24px 2px rgba(10,10,10,0.08);}' +

    // Hero BREATH (Goal 2) — hero-live ONLY, i.e. only when the hero sits
    // under YOUR WAVE: taste is set, so "for you" is a claim the row
    // header already makes truthfully. The no-taste hero stays static on
    // purpose — a pulse saying "for you" to someone who never gave taste
    // is the overclaim this gate exists to remove. The glow is a separate
    // ::before layer so ONLY ITS OPACITY animates (compositor-cheap;
    // animating box-shadow repaints every frame and janks the carousel)
    // while the ::after frame above holds rock-steady. ~3.8s full swell,
    // 25%↔100% — the "breathe" rhythm Marco picked in visual review; peak
    // alphas (.16 dark / .10 light) + duration are FLAGGED for Mav, tune
    // there not here. Reduced-motion: no animation, resting glow at .7 —
    // the state is still marked, nothing moves (Iris).
    'article[data-emphasis="hero-live"]::before{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;z-index:1;box-shadow:inset 0 0 36px 6px rgba(255,255,255,0.16);opacity:.25;animation:onsumn-hero-breathe 3.8s ease-in-out infinite;}' +
    ':root[data-theme="light"] article[data-emphasis="hero-live"]::before{box-shadow:inset 0 0 30px 5px rgba(10,10,10,0.10);}' +
    '@keyframes onsumn-hero-breathe{0%,100%{opacity:.25}50%{opacity:1}}' +
    '@media (prefers-reduced-motion:reduce){article[data-emphasis="hero-live"]::before{animation:none;opacity:.7;}}';

  document.head.appendChild(style);
})();
